/**
 * Relay Extensions — handles BeeOS-specific Canvas Relay binary messages
 * that are outside the A2UI specification.
 *
 * Binary protocol:
 *   0x01 — Awareness (editor is editing a surface)
 *   0x02 — Presence (agent online/offline)
 *   0x03 — Editors list (full list of connected editors)
 */

import type { EditorAwareness, EditorSnapshot } from "./types";

// Binary protocol constants
const MSG_AWARENESS = 0x01;
const MSG_PRESENCE = 0x02;
const MSG_EDITORS = 0x03;

export type AwarenessChangeListener = (awareness: Map<string, EditorAwareness>) => void;
export type PresenceChangeListener = (presence: Map<string, boolean>) => void;
export type EditorsChangeListener = (editors: EditorSnapshot[]) => void;

/**
 * Manages BeeOS-specific relay extension state: awareness, presence, editors.
 * Separate from the A2UI surface state managed by Google's MessageProcessor.
 */
export class RelayExtensions {
  private editorAwareness = new Map<string, EditorAwareness>();
  private agentPresence = new Map<string, boolean>();
  private editorsList: EditorSnapshot[] = [];

  private awarenessListeners = new Set<AwarenessChangeListener>();
  private presenceListeners = new Set<PresenceChangeListener>();
  private editorsListeners = new Set<EditorsChangeListener>();

  subscribeAwareness(listener: AwarenessChangeListener): () => void {
    this.awarenessListeners.add(listener);
    return () => this.awarenessListeners.delete(listener);
  }

  subscribePresence(listener: PresenceChangeListener): () => void {
    this.presenceListeners.add(listener);
    return () => this.presenceListeners.delete(listener);
  }

  subscribeEditors(listener: EditorsChangeListener): () => void {
    this.editorsListeners.add(listener);
    return () => this.editorsListeners.delete(listener);
  }

  getEditorAwareness(): Map<string, EditorAwareness> {
    return this.editorAwareness;
  }

  getAgentPresence(): Map<string, boolean> {
    return this.agentPresence;
  }

  getEditors(): EditorSnapshot[] {
    return this.editorsList;
  }

  reset(): void {
    this.editorAwareness.clear();
    this.agentPresence.clear();
    this.editorsList = [];
    this.notifyAwareness();
    this.notifyPresence();
    this.notifyEditors();
  }

  /**
   * Try to process a binary message from the relay.
   * Returns true if the message was handled (awareness, presence, or editors).
   */
  processMessage(data: Uint8Array): boolean {
    if (data.length < 2) return false;

    switch (data[0]) {
      case MSG_AWARENESS:
        return this.processAwareness(data);
      case MSG_PRESENCE:
        return this.processPresence(data);
      case MSG_EDITORS:
        return this.processEditors(data);
      default:
        return false;
    }
  }

  private processAwareness(data: Uint8Array): boolean {
    try {
      const json = new TextDecoder().decode(data.subarray(1));
      const payload = JSON.parse(json) as {
        agentId?: string;
        editingSurface?: string;
        timestamp?: number;
      };
      if (!payload.agentId) return false;

      if (!payload.editingSurface) {
        this.editorAwareness.delete(payload.agentId);
      } else {
        this.editorAwareness.set(payload.agentId, {
          agentId: payload.agentId,
          editingSurface: payload.editingSurface,
          timestamp: payload.timestamp ?? Date.now(),
        });
      }
      this.notifyAwareness();
      return true;
    } catch {
      return false;
    }
  }

  private processPresence(data: Uint8Array): boolean {
    try {
      const json = new TextDecoder().decode(data.subarray(1));
      const payload = JSON.parse(json) as { agentId?: string; online?: boolean };
      if (!payload.agentId || typeof payload.online !== "boolean") return false;
      this.agentPresence.set(payload.agentId, payload.online);
      this.notifyPresence();
      return true;
    } catch {
      return false;
    }
  }

  private processEditors(data: Uint8Array): boolean {
    try {
      const json = new TextDecoder().decode(data.subarray(1));
      const editors = JSON.parse(json) as EditorSnapshot[];
      if (!Array.isArray(editors)) return false;
      this.editorsList = editors;
      this.notifyEditors();
      return true;
    } catch {
      return false;
    }
  }

  private notifyAwareness(): void {
    for (const listener of this.awarenessListeners) {
      try { listener(this.editorAwareness); } catch { /* isolate */ }
    }
  }

  private notifyPresence(): void {
    for (const listener of this.presenceListeners) {
      try { listener(this.agentPresence); } catch { /* isolate */ }
    }
  }

  private notifyEditors(): void {
    for (const listener of this.editorsListeners) {
      try { listener(this.editorsList); } catch { /* isolate */ }
    }
  }
}
